import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { mutate } from "swr";
import ButtonSpinner from "src/components/Loader/ButtonSpinner";
import LoadingSpinner from "src/components/Loader/LoadingSpinner";
import { useClientSignUpSteps } from "src/lib/store/hirer-steps-store";
import ErrorBox from "../components/ErrorBox";
import { Field } from "../components/field";
import FileDropZone from "../components/FileDropZone";
import StepLayout from "../layout";
import { useTypedSelector, store } from "src/lib/store/store";
import { selectUserState } from "src/lib/features/auth/authSlice";
import JobLocationFields from "src/components/JobLocation/JobLocationFields";
import {
  emptyJobLocation,
  fromJobRow,
  toJobLocationPayload,
  validateJobLocation,
} from "src/components/JobLocation/jobLocation";

// import { continents, countries, languages } from 'countries-list'
// import { getCountryCode, getCountryData, getCountryDataList, getEmojiFlag } from 'countries-list'


const FieldsName = {
  DESCRIPTION: "description",
  DESCRIPTION_DOC: "desc_doc",
  NO_OF_TALENT: "no_of_talent",
  BUDGET: "budget",
  PAYMENT_TYPE: "payment_type",
}

export default function HirerExtrasDetailsStep({ handleClick, close, editData }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [files, setFiles] = useState([]);
  const { user } = useTypedSelector(selectUserState)
  const {
    basics,
    userLevel,
    jobType,
    userStack,
    userSkills,
    userLanguages,
    userFrameworks,
    extras,
    updateExtras,
    clearStore,
  } = useClientSignUpSteps()

  // Work arrangement + the place matching gates on. Kept outside
  // react-hook-form because it is a structured value, not an input string.
  const [jobLocation, setJobLocation] = useState(() =>
    editData ? fromJobRow(editData) : extras.jobLocation ?? emptyJobLocation()
  );
  const [jobLocationError, setJobLocationError] = useState(null);



  const formMethods = useForm({
    defaultValues: {
      [FieldsName.DESCRIPTION]: extras[FieldsName.DESCRIPTION] ?? "",
      [FieldsName.DESCRIPTION_DOC]: "",
      [FieldsName.NO_OF_TALENT]: extras[FieldsName.NO_OF_TALENT] ?? "",
      [FieldsName.BUDGET]: extras[FieldsName.BUDGET] ?? "",
      [FieldsName.PAYMENT_TYPE]: extras[FieldsName.PAYMENT_TYPE] ?? "",
    },
  });

  const {
    handleSubmit,
    register,
    setValue,
    watch,
    reset,
    formState: { errors: formErrors },
  } = formMethods;


  const paymentCategorizations = useMemo(() => {
    // Both new pay periods are paid at intervals while the talent delivers.
    return {
      weekly: "retainer",
      monthly: "retainer",
      // Older jobs being edited may still carry these.
      fixedcost: "milestone-based",
      retainer: "retainer",
    };
  }, []);

  const paymentTypes = useMemo(() => {
    return {
      monthly: "Monthly (Pay talent at the end of every month)",
      weekly: "Weekly (Pay talent at the end of every week)",
    };
  }, []);

  const convertedAmount = useMemo(() => {
    // if (values.budget == "") return 0;
    // const converted = Number.parseInt(values.budget) * 1486.34;
    // return new Intl.NumberFormat("en-US", {
    //   style: "currency",
    //   currency: "USD",
    // }).format(converted);
  }, ['values.budget']);

  const priceRange = useMemo(() => {
    if (userLevel) return "";
    return userLevel ? `$${userLevel.lower_price} - $${userLevel.higher_price}` : "";
  }, [userLevel]);

  const submit = handleSubmit(async (data) => {
    if (isSubmitting) return;

    const locationProblem = validateJobLocation(jobLocation);
    setJobLocationError(locationProblem);
    if (locationProblem) return;

    setIsSubmitting(true);
    const locationPayload = toJobLocationPayload(jobLocation);

    const formatWorkType = (value) => {
      if (!value) return "Contract";

      return value
        .split("-")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join("-");
    };

    const formData = new FormData();

    formData.append("companyName", user?.userCompany?.name);
    formData.append("phone", user.phone);
    formData.append("email", user.email);

    // Handle custom stack (role)
    const stackValue = userStack?.id?.toString().startsWith('custom-') ? userStack.name : userStack;
    formData.append("stack", stackValue);

    userLanguages.forEach((language, index) => {
      if (userLanguages[index] !== undefined) {
        const val = language?.id?.toString().startsWith('custom-') ? language?.name : language?.id;
        formData.append(`language${++index}`, val);
      }
    });

    userFrameworks.forEach((framework, index) => {
      if (userFrameworks[index] !== undefined) {
        const val = framework?.id?.toString().startsWith('custom-') ? framework?.name : framework?.id;
        formData.append(`framework${++index}`, val);
      }
    });

    userSkills.forEach((skill, index) => {
      if (userSkills[index] !== undefined) {
        const val = skill?.id?.toString().startsWith('custom-') ? skill?.name : skill?.id;
        formData.append(`skill${++index}`, val);
      }
    });

    /* On an edit whose stored level matches none of the price rows,
       `userLevel` is undefined; reading through it here threw before the
       request was ever sent. */
    formData.append(
      "salary_range",
      userLevel
        ? `$${userLevel.lower_price} - $${userLevel.higher_price}`
        : editData?.salary_range ?? ""
    );
    files.at(0) && formData.append("jobDescription", files.at(0));
    formData.append("jobDetails", data.description);
    formData.append("no_of_devs", data.no_of_talent);
    // formData.append("workType", "Contract");
    formData.append(
      "workType",
      jobType === "fulltime" ? "Full-time" : "Contract"
    );
    formData.append("paymentType", data.payment_type);
    formData.append(
      "paymentCategorization",
      paymentCategorizations[data.payment_type]
    );
    formData.append("projectBudget", data.budget);
    formData.append("level", userLevel?.seniority ?? editData?.level ?? "");
    formData.append("companyLocation", data.location);
    files.at(0) && formData.append("file", files.at(0));
    formData.append(
      "iso_country_code",
      user?.userCurrency?.country_iso_2
    );
    Object.entries(locationPayload).forEach(([key, val]) => formData.append(key, val));


    // USE FORM DATA.
    try {
      const isEditing = !!editData
      const url = isEditing
        ? `${process.env.REACT_APP_BASE_API_URL}/api/v1/comp/jobs/${editData.id}/edit`
        : `${process.env.REACT_APP_BASE_API_URL}/api/v1/comp/hireContact`

      const token = store.getState()?.auth?.accessToken || ''
      const headers = isEditing ? { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } : {}

      const formatField = (arr, prefix) => {
        const out = {}
        arr.forEach((item, idx) => {
          const val = item?.id?.toString().startsWith('custom-') ? item?.name : item?.id
          out[`${prefix}${idx + 1}`] = val ?? null
        })
        return out
      }

      const body = isEditing ? JSON.stringify({
        stack: typeof userStack === 'string' ? userStack : userStack?.name || userStack,
        level: userLevel?.seniority || editData.level,
        workType: jobType === "fulltime" ? "Full-time" : "Contract",
        salary_range: userLevel ? `$${userLevel.lower_price} - $${userLevel.higher_price}` : editData.salary_range,
        project_budget: data.budget,
        jobDescription: data.description,
        no_of_devs: data.no_of_talent,
        paymentType: data.payment_type,
        paymentCategorization: paymentCategorizations[data.payment_type],
        ...locationPayload,
        ...formatField(userLanguages, 'language'),
        ...formatField(userFrameworks, 'framework'),
        ...formatField(userSkills, 'skill'),
      }) : formData

      const response = await fetch(url, {
        method: isEditing ? "PUT" : "POST",
        headers: isEditing ? headers : undefined,
        body,
      })
      const result = await response?.json();
      if (result.status == true) {
        clearStore();
        close && close()
        if (isEditing) {
          window.location.reload()
        } else {
          toast.success("Job submitted for review. You can track its status under Hire Requests.")
          // Refresh the cached Hire Requests list so the new job shows up.
          mutate((key) => Array.isArray(key) && key[0] === "/comp/getJobDetails")
        }
      } else {
        toast.error(result?.message ?? "Couldn't submit your job. Please try again.")
      }
    } catch (error) {
      toast.error("Couldn't submit your job. Please check your connection and try again.")
    } finally {
      setIsSubmitting(false)
    }
  })

  useEffect(() => {
    updateExtras({
      [FieldsName.DESCRIPTION]: watch(FieldsName.DESCRIPTION),
      // [FieldsName.DESCRIPTION_DOC]: "",
      [FieldsName.NO_OF_TALENT]: watch(FieldsName.NO_OF_TALENT),
      [FieldsName.BUDGET]: watch(FieldsName.BUDGET),
      [FieldsName.PAYMENT_TYPE]: watch(FieldsName.PAYMENT_TYPE),
      jobLocation,
    })
  }, [
    watch(FieldsName.DESCRIPTION),
    watch(FieldsName.NO_OF_TALENT),
    watch(FieldsName.BUDGET),
    watch(FieldsName.PAYMENT_TYPE),
    jobLocation,
  ])

  useEffect(() => {
    if (jobType === 'fulltime') {
      setValue(FieldsName.PAYMENT_TYPE, 'monthly');
    }
  }, [jobType, setValue]);

  // Pre-fill form fields when editing. The field names have to match what the
  // job endpoint actually returns — see the note in ../index.jsx.
  useEffect(() => {
    if (!editData) return
    setValue(FieldsName.DESCRIPTION, editData.jobDetails || editData.jobDescription || '')
    setValue(FieldsName.NO_OF_TALENT, editData.no_of_devs || 1)
    setValue(
      FieldsName.BUDGET,
      editData.project_budget ?? editData.projectBudget ?? editData.project_amount ?? ''
    )
    // Only weekly/monthly are offered now; anything older falls back to monthly.
    const existingPaymentType = editData.payment_type || editData.paymentType
    setValue(FieldsName.PAYMENT_TYPE, paymentTypes[existingPaymentType] ? existingPaymentType : 'monthly')
  }, [editData, setValue])

  // console.log('User details:', user)

  return (
    <>
      <StepLayout
        title="More Details"
        description="Share more details about the role"
        plainChildren={true}
      >
        {(
          <div>
            <div className="flex flex-col space-y-4">
              <Field
                label={"Job Description"}
                errorMessage={formErrors[FieldsName.DESCRIPTION]?.message}
                htmlFor={FieldsName.DESCRIPTION}
              >
                <textarea
                  id={FieldsName.DESCRIPTION}
                  {...register(FieldsName.DESCRIPTION, {
                    required: "Description is required",
                  })}
                  placeholder="Brief description of the job"
                  rows={5}
                  className="border-2 resize-none p-2 text-sm rounded-md"
                />
              </Field>

              <Field
                label={"Upload Job Description File"}
                errorMessage={formErrors[FieldsName.DESCRIPTION_DOC]?.message}
                htmlFor={FieldsName.DESCRIPTION_DOC}
              >
                <FileDropZone
                  inputId={"file-upload"}
                  values={files}
                  onChange={(files, error) => {
                    setValue(FieldsName.DESCRIPTION_DOC, 'entered')
                    if (!error) setFiles(files);
                  }}
                  limit={1}
                  label={
                    files.length < 1
                      ? "Upload Job Description"
                      : "Replace Job Description"
                  }
                  accept={".pdf,docx,docs"}
                />
              </Field>

              <Field
                label={"No of Talent"}
                errorMessage={formErrors[FieldsName.NO_OF_TALENT]?.message}
                htmlFor={FieldsName.NO_OF_TALENT}
              >
                <input
                  id={FieldsName.NO_OF_TALENT}
                  {...register(FieldsName.NO_OF_TALENT, {
                    required: "Number of talent is required",
                  })}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  placeholder="Enter the number of talents you need"
                  className="py-2 px-1 text-sm  border-b-2"
                />
              </Field>

              <JobLocationFields
                value={jobLocation}
                onChange={(next) => { setJobLocation(next); setJobLocationError(null); }}
                error={jobLocationError}
              />


              {jobType !== 'fulltime' && (
                <Field
                  label={"Payment Type"}
                  errorMessage={formErrors[FieldsName.PAYMENT_TYPE]?.message}
                  htmlFor={FieldsName.PAYMENT_TYPE}
                >
                  <select
                    id={FieldsName.PAYMENT_TYPE}
                    {...register(FieldsName.PAYMENT_TYPE, {
                      required: jobType !== 'fulltime' ? "Payment type is required" : false,
                    })}
                    onChange={(event) => setValue(FieldsName.PAYMENT_TYPE, event.target.value)}
                    className={`py-2 px-1 text-sm border-0 border-b-2  ${watch(FieldsName.PAYMENT_TYPE) === ""
                      ? "text-muted-foreground"
                      : "text-gray-700"
                      }`}
                  >
                    <option value="" className="text-muted-foreground  break-all">
                      Select payment type
                    </option>
                    {Object.entries(paymentTypes).map(([key, value], idx) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              <Field
                label={"Payment Budget"}
                errorMessage={formErrors[FieldsName.BUDGET]?.message}
                htmlFor={FieldsName.BUDGET}
                helperText={
                  <p className="!text-normal md:hidden !text-xs text-muted-foreground flex-1 flex gap-2">
                    {user?.userCompany.currency}{" "}
                    <span className="text-secondary">{convertedAmount}</span>
                  </p>
                }
              >
                <div className="text-sm border-0 border-b-2 flex gap-2 items-center focus-within:outline outline-2">
                  {/* <p className="px-3 border-e-2 text-muted-foreground">NGN</p> */}
                  <p className="px-3 border-e-2 text-muted-foreground">
                    {user?.userCompany?.currency}
                  </p>

                  <input
                    id={FieldsName.BUDGET}
                    {...register(FieldsName.BUDGET, {
                      required: "Budget is required",
                    })}
                    type="number"
                    placeholder="Enter amount"
                    className="p-2 w-full outline-none"
                  />

                  {/* <p className="text-sm px-3 text-muted-foreground flex-1 gap-2 hidden md:flex">
                    USD{" "}
                    <span className="text-secondary">{convertedAmount}</span>
                  </p> */}
                </div>
              </Field>
            </div>

            <div className="mt-8 flex justify-between">
              <button
                onClick={() => {
                  handleClick("");
                }}
                type="button"
                className={
                  "bg-surface hover:bg-gray-200 rounded-md p-3 cursor-pointer"
                }
              >
                Previous
              </button>

              <button
                onClick={submit}
                disabled={isSubmitting}
                className="flex items-center bg-primary text-white rounded-md px-4 cursor-pointer"
              >
                {editData ? "Update Job Details" : "Submit"}
                {isSubmitting && <ButtonSpinner />}
              </button>
            </div>
          </div>
        )}
      </StepLayout>
    </>
  );
}
